// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import type { LocationCompanyType } from '@/constants/location-company-type';
import type { Sort } from '@/constants/sort';
import type { ReturnOrderReason } from '@/return-order/api/types';
import type { ReturnOrderSortProperty } from '@/return-order/hooks/use-return-orders';
import { Chip } from '@mui/material';
import { ComponentProps } from 'react';
import { z } from 'zod';

// =============================================================================
// Schemas + types
// =============================================================================

// The line status is a generated column computed from quantities, so it never
// holds the order-level CANCELLED or AWAITING_3PL.
export const roLineItemStatusSchema = z.enum([
  'DRAFT',
  'OPEN_PARTIAL',
  'OPEN_FULL',
  'OPEN_OVERAGE',
  'SHIPPED_PARTIAL',
  'SHIPPED_FULL',
  'SHIPPED_OVERAGE',
  'DELIVERED_PARTIAL',
  'DELIVERED_FULL',
  'DELIVERED_OVERAGE',
  'RECEIVED_PARTIAL',
  'RECEIVED_FULL',
  'RECEIVED_OVERAGE',
]);

export type ROLineItemStatus = z.infer<typeof roLineItemStatusSchema>;

export const roPhaseSchema = z.enum([
  'draft',
  'awaiting_3pl',
  'cancelled',
  'open',
  'shipped',
  'delivered',
  'received',
]);
export type ROPhase = z.infer<typeof roPhaseSchema>;

export const roSubstatusSchema = z.enum(['partial', 'full', 'overage']);
export type ROSubstatus = z.infer<typeof roSubstatusSchema>;

export const roStatusSchema = z.enum([
  'OPEN_PARTIAL',
  'OPEN_FULL',
  'OPEN_OVERAGE',
  'SHIPPED_PARTIAL',
  'SHIPPED_FULL',
  'SHIPPED_OVERAGE',
  'DELIVERED_PARTIAL',
  'DELIVERED_FULL',
  'DELIVERED_OVERAGE',
  'RECEIVED_PARTIAL',
  'RECEIVED_FULL',
  'RECEIVED_OVERAGE',
  'DRAFT',
  'AWAITING_3PL',
  'CANCELLED',
]);
export type ROStatus = z.infer<typeof roStatusSchema>;

// =============================================================================
// Chip + phase config
// =============================================================================

type ChipColor = ComponentProps<typeof Chip>['color'];
type ChipVariant = ComponentProps<typeof Chip>['variant'] | 'text';

interface ChipConfig {
  color: ChipColor;
  variant: ChipVariant;
}

interface PhaseConfig {
  label: string;
  backendFilters: ROStatus[];
  displayOrder: number;
  chipConfig: ChipConfig;
}

export const PHASE_CONFIG: Record<ROPhase, PhaseConfig> = {
  draft: {
    label: 'Draft',
    backendFilters: ['DRAFT'],
    displayOrder: 0,
    chipConfig: { color: 'default', variant: 'text' },
  },
  awaiting_3pl: {
    label: 'Awaiting 3PL',
    backendFilters: ['AWAITING_3PL'],
    displayOrder: 1,
    chipConfig: { color: 'default', variant: 'filled' },
  },
  open: {
    label: 'Open',
    backendFilters: ['OPEN_PARTIAL', 'OPEN_FULL', 'OPEN_OVERAGE'],
    displayOrder: 2,
    chipConfig: { color: 'default', variant: 'filled' },
  },
  shipped: {
    label: 'Shipped',
    backendFilters: ['SHIPPED_PARTIAL', 'SHIPPED_FULL', 'SHIPPED_OVERAGE'],
    displayOrder: 3,
    chipConfig: { color: 'info', variant: 'filled' },
  },
  delivered: {
    label: 'Delivered',
    backendFilters: [
      'DELIVERED_PARTIAL',
      'DELIVERED_FULL',
      'DELIVERED_OVERAGE',
    ],
    displayOrder: 4,
    chipConfig: { color: 'primary', variant: 'filled' },
  },
  received: {
    label: 'Received',
    backendFilters: ['RECEIVED_PARTIAL', 'RECEIVED_FULL', 'RECEIVED_OVERAGE'],
    displayOrder: 5,
    chipConfig: { color: 'success', variant: 'filled' },
  },
  cancelled: {
    label: 'Cancelled',
    backendFilters: ['CANCELLED'],
    displayOrder: 6,
    chipConfig: { color: 'error', variant: 'outlined' },
  },
};

// =============================================================================
// Status predicates
// =============================================================================

export function isFullyReceived(status: ROLineItemStatus | ROStatus): boolean {
  return status === 'RECEIVED_FULL' || status === 'RECEIVED_OVERAGE';
}

export function isReceived(status: ROLineItemStatus | ROStatus): boolean {
  return status.startsWith('RECEIVED');
}

export function isCancelled(status: ROLineItemStatus | ROStatus): boolean {
  return status === 'CANCELLED';
}

export function isProcessed(status: ROLineItemStatus | ROStatus): boolean {
  return isReceived(status) || isCancelled(status);
}

// =============================================================================
// Status → phase/modifier mapping
// =============================================================================

const STATUS_TO_PHASE: Record<ROStatus, ROPhase> = Object.fromEntries(
  Object.entries(PHASE_CONFIG).flatMap(([phase, config]) =>
    config.backendFilters.map((status) => [status, phase])
  )
) as Record<ROStatus, ROPhase>;

export const PHASES_WITH_MODIFIERS: ReadonlySet<ROPhase> = new Set<ROPhase>([
  'shipped',
  'delivered',
  'received',
]);

export interface ParsedStatus {
  phase: ROPhase;
  modifier: ROSubstatus | null;
}

export function parseStatus(status: ROLineItemStatus | ROStatus): ParsedStatus {
  const phase = STATUS_TO_PHASE[status];
  if (!PHASES_WITH_MODIFIERS.has(phase)) return { phase, modifier: null };
  const parts = status.split('_');
  const suffix = parts[parts.length - 1].toLowerCase();
  const parsed = roSubstatusSchema.safeParse(suffix);
  return { phase, modifier: parsed.success ? parsed.data : null };
}

export function getModifierVariant(
  phase: ROPhase,
  modifier: ROSubstatus | null
): ChipVariant {
  if (modifier === 'partial') return 'outlined';
  return PHASE_CONFIG[phase].chipConfig.variant;
}

// Audit-log labels for a return order status. Partial states read as "Partially received"; full and overage
// states collapse to the plain phase ("Open", "Received"), so the audit trail
// never surfaces the overage modifier. Matches FO_STATUS_AUDIT_LABELS, so a
// status every order kind shares reads the same way on each trail.
export const RO_STATUS_AUDIT_LABELS: Record<ROStatus, string> =
  Object.fromEntries(
    roStatusSchema.options.map((status) => {
      const parsed = parseStatus(status);
      const phaseLabel = PHASE_CONFIG[parsed.phase].label;
      if (parsed.modifier === 'partial') {
        return [status, `Partially ${phaseLabel.toLowerCase()}`];
      }
      return [status, phaseLabel];
    })
  ) as Record<ROStatus, string>;

export const RO_STATUS_NOTIFICATION_LABELS: Record<ROStatus, string> =
  Object.fromEntries(
    roStatusSchema.options.map((status) => {
      const parsed = parseStatus(status);
      const phaseLabel = PHASE_CONFIG[parsed.phase].label;
      if (parsed.modifier === 'partial') {
        return [status, `Partially ${phaseLabel.toLowerCase()}`];
      }
      if (parsed.modifier === 'overage') {
        return [status, `${phaseLabel} with overage`];
      }
      return [status, phaseLabel];
    })
  ) as Record<ROStatus, string>;

// =============================================================================
// Line item tooltips
// =============================================================================

export const LINE_ITEM_STATUS_TOOLTIPS: Record<ROLineItemStatus, string> = {
  DRAFT:
    'This line item is in draft (no shipments have been opened against it)',
  OPEN_PARTIAL:
    'This line item is partially open (shipments have been opened with less than the requested quantity)',
  OPEN_FULL:
    'This line item is fully open (shipments have been opened with exactly the requested quantity)',
  OPEN_OVERAGE:
    'This line item is open with an overage (shipments have been opened with more than the requested quantity)',
  SHIPPED_PARTIAL:
    'This line item is partially shipped (shipments have been shipped with less than the requested quantity)',
  SHIPPED_FULL:
    'This line item is fully shipped (shipments have been shipped with exactly the requested quantity)',
  SHIPPED_OVERAGE:
    'This line item is shipped with an overage (shipments have been shipped with more than the requested quantity)',
  DELIVERED_PARTIAL:
    'This line item is partially delivered (shipments have been delivered with less than the requested quantity)',
  DELIVERED_FULL:
    'This line item is fully delivered (shipments have been delivered with exactly the requested quantity)',
  DELIVERED_OVERAGE:
    'This line item is delivered with an overage (shipments have been delivered with more than the requested quantity)',
  RECEIVED_PARTIAL:
    'This line item is partially received (shipments have been received with less than the requested quantity)',
  RECEIVED_FULL:
    'This line item is fully received (shipments have been received with exactly the requested quantity)',
  RECEIVED_OVERAGE:
    'This line item is received with an overage (shipments have been received with more than the requested quantity)',
};

// =============================================================================
// Phase progression
// =============================================================================

const TERMINAL_PHASES: readonly ROPhase[] = ['received', 'cancelled'];

const PHASE_PROGRESSION: readonly ROPhase[] = roPhaseSchema.options.filter(
  (p: ROPhase) => p !== 'cancelled'
);

export function getPhaseProgression(isThirdPartyEnabled: boolean): ROPhase[] {
  return isThirdPartyEnabled
    ? [...PHASE_PROGRESSION]
    : PHASE_PROGRESSION.filter((s) => s !== 'awaiting_3pl');
}

export function getTerminalPhases(): readonly ROPhase[] {
  return TERMINAL_PHASES;
}

// =============================================================================
// RO-specific
// =============================================================================

export const RO_STATUS_TOOLTIPS: Record<ROStatus, string> = {
  DRAFT:
    'This RO is in draft (no shipments have been opened against any line item)',
  AWAITING_3PL: 'This RO is awaiting an update from your 3PL',
  OPEN_PARTIAL:
    'This RO is partially open (shipments have been opened with less than the requested quantity against one or more line items)',
  OPEN_FULL:
    'This RO is fully open (shipments have been opened with exactly the requested quantity against all line items)',
  OPEN_OVERAGE:
    'This RO is open with an overage (shipments have been opened with the requested quantity on all line items, and more than the requested quantity on at least one line item)',
  SHIPPED_PARTIAL:
    'This RO is partially shipped (shipments have been shipped with less than the requested quantity against one or more line items)',
  SHIPPED_FULL:
    'This RO is fully shipped (shipments have been shipped with exactly the requested quantity against all line items)',
  SHIPPED_OVERAGE:
    'This RO is shipped with an overage (shipments have been shipped with the requested quantity on all line items, and more than the requested quantity on at least one line item)',
  DELIVERED_PARTIAL:
    'This RO is partially delivered (shipments have been delivered with less than the requested quantity against one or more line items)',
  DELIVERED_FULL:
    'This RO is fully delivered (shipments have been delivered with exactly the requested quantity against all line items)',
  DELIVERED_OVERAGE:
    'This RO is delivered with an overage (shipments have been delivered with the requested quantity on all line items, and more than the requested quantity on at least one line item)',
  RECEIVED_PARTIAL:
    'This RO is partially received (shipments have been received with less than the requested quantity against one or more line items)',
  RECEIVED_FULL:
    'This RO is fully received (shipments have been received with exactly the requested quantity against all line items)',
  RECEIVED_OVERAGE:
    'This RO is received with an overage (shipments have been received with the requested quantity on all line items, and more than the requested quantity on at least one line item)',
  CANCELLED: 'This RO is cancelled',
};

export interface ReturnOrderFilter {
  status?: ROPhase;
  subStatuses?: ROSubstatus[];
  reason?: ReturnOrderReason;
  searchTerm?: string;
  page?: number;
  pageSize?: number;
  sort?: Sort<ReturnOrderSortProperty>;
  /**
   * Locations the return order is shipping FROM. The from-location filter is
   * the OR of three independent dimensions: explicit location IDs, customer
   * IDs (any location assigned to one of these customers), and company types
   * (customer-site vs manufacturer-site).
   */
  fromLocationIds?: string[];
  fromCustomerIds?: string[];
  fromLocationCompanyTypes?: LocationCompanyType[];
  /**
   * Locations the return order is shipping TO. Mirrors the from-location
   * filter shape.
   */
  toLocationIds?: string[];
  toCustomerIds?: string[];
  toLocationCompanyTypes?: LocationCompanyType[];
}
